import { NavigateBefore, NavigateNext, ZoomIn, ZoomOut } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SearchIcon from '@mui/icons-material/Search';
import { Button, CircularProgress, IconButton, InputAdornment, Slider, TextField, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import type React from 'react';
import { type Dispatch, type KeyboardEvent, type SetStateAction, useState } from 'react';
import useStyles from 'src/components/PDF/PDFControlPanel/PDFControlPanelStyles';
import PDFPrinter from 'src/components/PDF/PDFPrinter/PDFPrinter';

interface PDFControlPanelProps {
  rotateClockwise: () => void;
  rotateCounterclockwise: () => void;
  numPages: number | null;
  pageNumber: number;
  setPageNumber: Dispatch<SetStateAction<number>>;
  scale: number;
  setScale: Dispatch<SetStateAction<number>>;
  pdfUrl: string;
  isOnMediaPage?: boolean;
  downloadLink?: string;
  searchText: string;
  setSearchText: (text: string) => void;
  matchCount: number;
  currentMatchIndex: number;
  goToNextMatch: () => void;
  goToPreviousMatch: () => void;
  isSearching: boolean;
}

const PDFControlPanel: React.FC<PDFControlPanelProps> = ({
  rotateClockwise,
  rotateCounterclockwise,
  numPages,
  pageNumber,
  setPageNumber,
  scale,
  setScale,
  pdfUrl,
  isOnMediaPage,
  searchText,
  setSearchText,
  matchCount,
  currentMatchIndex,
  goToNextMatch,
  goToPreviousMatch,
  isSearching,
}) => {
  const classes = useStyles({ isOnMediaPage: isOnMediaPage ?? false });

  const [goToPageInput, setGoToPageInput] = useState<string>('');

  const handleZoomIn = (): void => {
    setScale((scale) => scale + 0.1);
  };

  const handleZoomOut = (): void => {
    if (scale > 0.2) {
      setScale((scale) => scale - 0.1);
    }
  };

  const handleGoToPage = (): void => {
    const pageNum = parseInt(goToPageInput, 10);
    if (!Number.isNaN(pageNum) && pageNum > 0 && numPages && pageNum <= numPages) {
      setPageNumber(pageNum);
      setGoToPageInput('');
    }
  };

  const handleNextPage = (): void => {
    if (numPages && pageNumber < numPages) {
      setPageNumber((pageNumber) => pageNumber + 1);
    }
  };

  const handlePreviousPage = (): void => {
    if (pageNumber > 1) {
      setPageNumber((pageNumber) => pageNumber - 1);
    }
  };

  const handleFastRewind = (): void => {
    if (pageNumber > 10) {
      setPageNumber((pageNumber) => pageNumber - 10);
    }
  };

  const handleFastForward = (): void => {
    if (numPages && pageNumber < numPages - 10) {
      setPageNumber((pageNumber) => pageNumber + 10);
    }
  };

  const handleFirstPage = (): void => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
  };

  const handleLastPage = (): void => {
    if (numPages && pageNumber !== numPages) {
      setPageNumber(numPages);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        goToPreviousMatch();
      } else {
        goToNextMatch();
      }
    }
    if (e.key === 'Escape') {
      setSearchText('');
    }
  };

  const handleSliderChange = (_e: Event, newValue: number | number[]): void => {
    setScale((newValue as number) / 100);
  };

  const hasMatches = matchCount > 0;
  const showMatchInfo = searchText.length >= 2;

  return (
    <div className={classes.controlPanelWrapper}>
      <div className={classes.leftSidePanel}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <IconButton disabled={pageNumber <= 10} onClick={handleFastRewind} color="primary" size="large">
            <FastRewindIcon />
          </IconButton>
          <IconButton disabled={pageNumber === 1} onClick={handlePreviousPage} color="primary" size="large">
            <NavigateBefore />
          </IconButton>
          <Typography className={classes.pageNumberText}>
            Page {pageNumber} of {numPages}
          </Typography>
          <IconButton disabled={pageNumber === numPages} onClick={handleNextPage} color="primary" size="large">
            <NavigateNext />
          </IconButton>
          <IconButton
            disabled={numPages ? pageNumber >= numPages - 9 : true}
            onClick={handleFastForward}
            color="primary"
            size="large"
          >
            <FastForwardIcon />
          </IconButton>
          <Button
            className={classes.firstButton}
            disabled={pageNumber === 1}
            variant="contained"
            color="primary"
            onClick={handleFirstPage}
          >
            First Page
          </Button>
          <Button
            className={classes.lastButton}
            disabled={pageNumber === numPages}
            variant="contained"
            color="primary"
            onClick={handleLastPage}
          >
            Last Page
          </Button>
          <TextField
            className={classes.pdfTextFields}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
            placeholder="Go to page"
            value={goToPageInput}
            onChange={(e) => setGoToPageInput(e.target.value)}
            style={{ marginRight: '10px', width: '100px' }}
          />
          <Button
            className={classes.goButton}
            disabled={goToPageInput === '' || Number.isNaN(Number(goToPageInput))}
            variant="contained"
            color="primary"
            onClick={handleGoToPage}
          >
            Go
          </Button>
        </div>
        <div className={classes.rightSidePanel}>
          <div className={classes.searchContainer}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Search in PDF..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isSearching ? <CircularProgress size={18} /> : <SearchIcon fontSize="small" color="primary" />}
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    {showMatchInfo && (
                      <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
                        {hasMatches ? `${currentMatchIndex + 1}/${matchCount}` : '0/0'}
                      </Typography>
                    )}
                    <IconButton size="small" onClick={goToPreviousMatch} disabled={!hasMatches}>
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={goToNextMatch} disabled={!hasMatches}>
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </div>
          <IconButton className={classes.zoomOutButton} onClick={handleZoomOut} color="primary" size="large">
            <ZoomOut />
          </IconButton>
          <div className={classes.scaleAndSlider}>
            <Typography className={classes.zoomNumber}>{(scale * 100).toFixed()}%</Typography>
            <Slider
              value={scale * 100}
              onChange={handleSliderChange}
              min={20}
              max={200}
              style={{ width: '150px', margin: '0 10px' }}
            />
          </div>
          <IconButton className={classes.zoomInButton} onClick={handleZoomIn} color="primary" size="large">
            <ZoomIn />
          </IconButton>
          <Tooltip title="Rotate Clockwise">
            <IconButton onClick={rotateClockwise} color="primary" size="large">
              <RotateRightIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rotate Counterclockwise">
            <IconButton onClick={rotateCounterclockwise} color="primary" size="large">
              <RotateLeftIcon />
            </IconButton>
          </Tooltip>

          <div className={classes.printPDF}>
            <PDFPrinter pdfUrl={pdfUrl} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFControlPanel;
